import { useEffect, useState } from "react";

import { listActiveCategories } from "@/api/categories.api";
import { listThemesByCategory } from "@/api/themes.api";
import type { CategorySummary, ThemeSummary } from "@/types/catalog";

export interface UseCategoriesAndThemesResult {
  readonly categories: readonly CategorySummary[];
  readonly themes: readonly ThemeSummary[];
  readonly loadingCategories: boolean;
  readonly loadingThemes: boolean;
  readonly error: Error | null;
}

// CR-110: Data loader for the Generate-preview picker modal.
// - Categories load once on mount (picker assumes stable catalog during open).
// - Themes load whenever categoryId changes; prior fetch is aborted via
//   AbortController so rapid switches can't stomp the newest result
//   (synthesis §3).
// - Theme fetches page-exhaust (synthesis §4): loops until
//   total <= page * page_size, so a category with 100+ active themes still
//   renders every option.
export function useCategoriesAndThemes(
  categoryId: string | null,
): UseCategoriesAndThemesResult {
  const [categories, setCategories] = useState<readonly CategorySummary[]>([]);
  const [themes, setThemes] = useState<readonly ThemeSummary[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingThemes, setLoadingThemes] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Categories: load once on mount.
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setLoadingCategories(true);
    listActiveCategories(ctrl.signal)
      .then((page) => {
        if (cancelled) return;
        setCategories(page.items);
        setLoadingCategories(false);
      })
      .catch((err: Error) => {
        if (cancelled || ctrl.signal.aborted) return;
        setError(err);
        setLoadingCategories(false);
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  // Themes: refetch whenever categoryId changes. Aborts prior request so a
  // slow response for a stale category can't clobber the newest selection.
  useEffect(() => {
    if (categoryId === null) {
      setThemes([]);
      setLoadingThemes(false);
      return undefined;
    }
    const ctrl = new AbortController();
    let cancelled = false;

    async function loadAllPages(): Promise<void> {
      setLoadingThemes(true);
      setThemes([]);
      setError(null);
      try {
        const collected: ThemeSummary[] = [];
        let page = 1;
        // Upper bound guard — defensive against a misbehaving backend that
        // ever returns total_pages inconsistent with total/page_size.
        for (let guard = 0; guard < 50; guard += 1) {
          const resp = await listThemesByCategory(
            categoryId as string,
            page,
            ctrl.signal,
          );
          if (cancelled || ctrl.signal.aborted) return;
          collected.push(...resp.items);
          if (collected.length >= resp.total || resp.items.length === 0) {
            break;
          }
          page += 1;
        }
        if (cancelled || ctrl.signal.aborted) return;
        setThemes(collected);
        setLoadingThemes(false);
      } catch (err) {
        if (cancelled || ctrl.signal.aborted) return;
        setError(err as Error);
        setThemes([]);
        setLoadingThemes(false);
      }
    }

    void loadAllPages();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [categoryId]);

  return { categories, themes, loadingCategories, loadingThemes, error };
}
